import Choices from 'choices.js';
import Chart from 'chart.js/auto';

const createLegendItem = (chart, item) => {
    const li = document.createElement('li');
    li.tabIndex = 0;
    li.setAttribute('role', 'button');
    li.setAttribute('aria-pressed', item.hidden ? 'false' : 'true');

    const updateLegendState = () => {
        const isHidden = chart.getDatasetMeta(item.datasetIndex).hidden;
        li.setAttribute('aria-pressed', isHidden ? 'false' : 'true');
        label.style.textDecoration = isHidden ? 'line-through' : '';
    };

    const toggleDatasetVisibility = () => {
        const clicked = item.datasetIndex;
        const isSolo = chart.data.datasets.every((ds, i) =>
            i === clicked || chart.getDatasetMeta(i).hidden
        );
        chart.data.datasets.forEach((_, i) => {
            chart.getDatasetMeta(i).hidden = isSolo ? false : i !== clicked;
        });
        chart.update();
        updateLegendState();
    };

    li.addEventListener('click', toggleDatasetVisibility);
    li.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleDatasetVisibility();
        }
    });

    const isLine = item.strokeStyle && item.strokeStyle !== 'transparent';
    const dataset = chart.data.datasets[item.datasetIndex];

    const colorBox = document.createElement('span');

    colorBox.classList.add('bitcoin-calculator__legend__color');
    if (isLine) colorBox.classList.add('bitcoin-calculator__legend__color--line');
    colorBox.style.background = isLine ? item.strokeStyle : (dataset.legendBackground || item.fillStyle);

    const label = document.createElement('span');
    
    label.classList.add('bitcoin-calculator__legend__label');
    label.style.textDecoration = item.hidden ? 'line-through' : '';
    label.textContent = item.text;

    li.appendChild(colorBox);
    li.appendChild(label);
    return li;
};

const updateHtmlLegend = (chart, containerID) => {
    const ul = document.getElementById(containerID);
    
    if (!ul) return;

    while (ul.firstChild) ul.firstChild.remove();

    chart.options.plugins.legend.labels.generateLabels(chart)
        .filter(item => item.text !== 'Current transparent')
        .forEach(item => ul.appendChild(createLegendItem(chart, item)));
};

const htmlLegendPlugin = {
    id: 'htmlLegend',
    afterUpdate(chart, args, options) {
        updateHtmlLegend(chart, options.containerID);
    }
};

const calculator = {
    CONSTANTS: {
        FONT_FAMILY: 'Helvetica Now Display Fallback, Arial, sans-serif',
        ORANGE_COLOR: '#F98800',
        GRID_COLOR: 'rgba(200, 200, 200, 0.3)',
        TEXT_COLOR: '#81848A',
        TEXT_COLOR_DARK: '#212226',
        ACCENT_COLOR: '#14217E',
        GRADIENT_START: 'rgba(97, 99, 103, 0.5)',
        GRADIENT_END: 'rgba(97, 99, 103, 0)',
        CACHE_DURATION: 300000,
        MAX_CACHE_SIZE: 10,
        DEBOUNCE_DELAY: 250,
    },

    settings: {
        canvas: document.querySelector('.js-calculator'),
        selects: document.querySelectorAll('.js-calculator-select'),
        form: document.querySelector('.js-calculator-form'),
        inputAmount: document.querySelector('.js-amount-chart'),
        selectStartYear: document.querySelector('.js-start-select'),
        selectCurrency: document.querySelector('.js-currency-select'),
        selectInterval: document.querySelector('.js-interval-select'),
        currencyTable: document.querySelectorAll('.js-currency'),
        amountTable: document.querySelector('.js-amount'),
        bitcoinValueElement: document.querySelector('.js-bitcoin-value'),
        profitLossContainer: document.querySelector('.js-profit-loss'),
        profitLossAmount: document.querySelector('.js-profit-loss-amount'),
        profitLossPercentage: document.querySelector('.js-profit-loss-percentage'),
        bitcoinOwnValue: document.querySelector('.js-bitcoin-own-value'),
        bitcoinStartYear: 0,
        chartInstance: null,
        defaultCurrency: document.querySelector('.js-currency-select')?.value || 'EUR',
        defaultInterval: document.querySelector('.js-interval-select')?.value || 'week',
        defaultAmount: 100,
        dataCache: {},
        eventListeners: [],
        abortController: null,
        choicesInstances: [],
        isUpdating: false,
    },
    
    init() {
        if (!this.settings.canvas) return;

        this.settings.bitcoinStartYear = Number(this.settings.canvas.getAttribute('data-start-year')) || 2009;
        this.settings.labelInvested = this.settings.canvas.getAttribute('data-label-invested') || 'Invested Amount';
        this.settings.labelPortfolio = this.settings.canvas.getAttribute('data-label-portfolio') || 'Portfolio Value';
        this.settings.labelBtc = this.settings.canvas.getAttribute('data-label-btc') || 'BTC';

        if (this.settings.inputAmount?.value) {
            this.settings.defaultAmount = parseFloat(this.settings.inputAmount.value);
        }

        this.initSelect();
        this.setEvents();
        this.drawChart();
        this.listenForPriceUpdates();
    },

    destroy() {
        this.settings.eventListeners.forEach(({ element, event, handler }) => {
            element.removeEventListener(event, handler);
        });
        this.settings.eventListeners = [];
        
        if (this.settings.chartInstance) {
            this.settings.chartInstance.destroy();
            this.settings.chartInstance = null;
        }

        if (this.settings.abortController) {
            this.settings.abortController.abort();
        }

        if (this.settings.choicesInstances) {
            this.settings.choicesInstances.forEach(instance => {
                if (instance && typeof instance.destroy === 'function') {
                    instance.destroy();
                }
            });
            this.settings.choicesInstances = [];
        }
        
        // Clean up tooltip
        const tooltipEl = document.getElementById('chartjs-tooltip');
        if (tooltipEl) {
            tooltipEl.remove();
        }
    },

    debounce(func, wait) {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    },

    isMobile() {
        return window.matchMedia('(max-width: 768px)').matches;
    },

    initSelect() {
        if (!this.settings.selects) return;

        this.settings.selects.forEach(select => {
            const choicesInstance = new Choices(select, {
                searchEnabled: false,
                itemSelectText: '',
                shouldSort: false,
                classNames: {
                    selectedState: ['is-selected'],
                }
            });
            this.settings.choicesInstances.push(choicesInstance);
        });
    },

    setEvents() {
        if (this.settings.selectStartYear) {
            const startYearHandler = async () => {
                await this.updateChart();
                if (window.lastBitcoinPriceUpdate) {
                    this.handlePriceUpdate(window.lastBitcoinPriceUpdate);
                }
            };
            this.settings.selectStartYear.addEventListener('change', startYearHandler);
            this.settings.eventListeners.push({
                element: this.settings.selectStartYear,
                event: 'change',
                handler: startYearHandler
            });
        }

        if (this.settings.inputAmount) {
            const debouncedUpdateChart = this.debounce(() => {
                this.updateChart();
            }, this.CONSTANTS.DEBOUNCE_DELAY);

            this.settings.inputAmount.addEventListener('input', debouncedUpdateChart);
            this.settings.eventListeners.push({
                element: this.settings.inputAmount,
                event: 'input',
                handler: debouncedUpdateChart
            });
        }

        if (this.settings.selectInterval) {
            const intervalHandler = () => {
                this.updateChart();
            };
            this.settings.selectInterval.addEventListener('change', intervalHandler);
            this.settings.eventListeners.push({
                element: this.settings.selectInterval,
                event: 'change',
                handler: intervalHandler
            });
        }

        if (this.settings.selectCurrency) {
            const currencyHandler = () => {
                this.updateChart();
            };
            this.settings.selectCurrency.addEventListener('change', currencyHandler);
            this.settings.eventListeners.push({
                element: this.settings.selectCurrency,
                event: 'change',
                handler: currencyHandler
            });
        }

        const resizeHandler = this.debounce(() => {
            if (this.settings.chartInstance) {
                this.settings.chartInstance.resize();
            }
        }, this.CONSTANTS.DEBOUNCE_DELAY);

        window.addEventListener('resize', resizeHandler);
        this.settings.eventListeners.push({
            element: window,
            event: 'resize',
            handler: resizeHandler
        });
    },

    manageCacheSize() {
        const cacheKeys = Object.keys(this.settings.dataCache);
        if (cacheKeys.length > this.CONSTANTS.MAX_CACHE_SIZE) {
            const sortedKeys = cacheKeys.sort((a, b) => 
                this.settings.dataCache[a].timestamp - this.settings.dataCache[b].timestamp
            );
            
            const keysToRemove = sortedKeys.slice(0, cacheKeys.length - this.CONSTANTS.MAX_CACHE_SIZE);
            keysToRemove.forEach(key => delete this.settings.dataCache[key]);
        }
    },

    generateLabels(startYear) {
        const currentYear = new Date().getFullYear();
        const labels = [];
        
        if (startYear === currentYear) {
            const currentMonth = new Date().getMonth(); 
            const startMonth = Math.max(0, currentMonth - 6);
            
            for (let month = startMonth; month <= currentMonth; month++) {
                const date = new Date(currentYear, month);
                labels.push(date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
            }
        } else {
            for (let year = startYear; year <= currentYear; year++) {
                labels.push(year.toString());
            }
        }
        
        return labels;
    },

    createGradient(ctx, height) {
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, this.CONSTANTS.GRADIENT_START);
        gradient.addColorStop(1, this.CONSTANTS.GRADIENT_END);
        return gradient;
    },

    createChartGradient() {
        if (!this.settings.canvas) return null;
        
        const ctx = this.settings.canvas.getContext('2d');
        const canvasHeight = this.settings.chartInstance?.height || 
                           this.settings.canvas.clientHeight || 
                           this.settings.canvas.offsetHeight || 
                           400;
        
        if (this._lastCanvasHeight === canvasHeight && this._cachedGradient) {
            return this._cachedGradient;
        }
        
        this._lastCanvasHeight = canvasHeight;
        this._cachedGradient = this.createGradient(ctx, canvasHeight);
        return this._cachedGradient;
    },

    drawChartLabel(ctx, text, fillStyle, textX, textY) {
        ctx.save();
        ctx.font = `600 14px ${this.CONSTANTS.FONT_FAMILY}`;
        ctx.fillStyle = fillStyle;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'bottom';
        ctx.fillText(text, textX, textY);
        ctx.restore();
    },

    getLastDataPointY(chart, datasetIndex) {
        const meta = chart.getDatasetMeta(datasetIndex);
        if (!meta || !meta.data || meta.data.length === 0) return null;
        const lastElement = meta.data[meta.data.length - 1];
        return lastElement && typeof lastElement.y === 'number' ? lastElement.y : null;
    },

    getDatasetLabelColor(dataset) {
        const c = dataset.borderColor;
        if (c && c !== 'transparent') return c;
        return this.CONSTANTS.TEXT_COLOR;
    },

    getIntervalMs(interval) {
        const dayMs = 24 * 60 * 60 * 1000;
        const map = { day: dayMs, 'week': 7 * dayMs, '2-weeks': 14 * dayMs, 'month': 30.44 * dayMs };
        return map[interval] ?? 7 * dayMs;
    },

    /** Number of investments made by date. First investment at start; one more per completed interval. */
    getInvestmentCountByDate(startDate, date, interval) {
        if (date <= startDate) return 1;

        if (interval === 'month') {
            const months = (date.getFullYear() - startDate.getFullYear()) * 12
                         + (date.getMonth() - startDate.getMonth());
            return Math.max(1, months);
        }

        const intervalMs = this.getIntervalMs(interval);
        const elapsed = date - startDate;
        return Math.max(1, Math.floor(elapsed / intervalMs));
    },

    aggregatePriceData(prices, startYear) {
        if (!prices || prices.length === 0) return [];

        const currentYear = new Date().getFullYear();
        const isCurrentYear = startYear === currentYear;

        if (isCurrentYear) {
            return prices;
        } else {
            const yearlyData = {};

            prices.forEach(price => {
                const date = new Date(price.datetime);
                const year = date.getFullYear();
                
                if (year >= startYear && year <= currentYear) {
                    if (!yearlyData[year]) {
                        yearlyData[year] = { prices: [], year };
                    }
                    yearlyData[year].prices.push(price.price);
                }
            });

            return Object.keys(yearlyData).sort().map(year => {
                const data = yearlyData[year];
                const avgPrice = data.prices.reduce((sum, p) => sum + p, 0) / data.prices.length;
                return {
                    datetime: new Date(data.year, 6, 1).toISOString(),
                    price: avgPrice
                };
            });
        }
    },

    async getBitcoinHistoricalPrices(currency, startDateOrYear, forOrangeLine = false) {
        const fromDate = new Date(startDateOrYear);
        fromDate.setUTCHours(0, 0, 0, 0);
        const startYear = fromDate.getFullYear();

        const cacheKey = `historical-${currency}-${startDateOrYear}${forOrangeLine ? '-raw' : ''}`;

        if (this.settings.dataCache[cacheKey] &&
            (Date.now() - this.settings.dataCache[cacheKey].timestamp) < this.CONSTANTS.CACHE_DURATION) {
            const cachedData = this.settings.dataCache[cacheKey].data;
            if (Array.isArray(cachedData)) {
                return { prices: cachedData, currentPrice: null };
            }
            return cachedData;
        }

        if (this.settings.abortController) {
            this.settings.abortController.abort();
        }
        this.settings.abortController = new AbortController();

        const timeoutId = setTimeout(() => {
            this.settings.abortController.abort();
        }, 10000);

        try {
            const now = new Date();
            const monthsDiff = (now.getFullYear() - fromDate.getFullYear()) * 12 + (now.getMonth() - fromDate.getMonth());
            const yearsDiff = Math.max(1, Math.ceil(monthsDiff / 12));

            const toDate = now.toISOString();
            const granularity = 'DAILY';
            const maxDataPoints = 500;

            if (!currency || !['USD', 'EUR', 'CHF'].includes(currency)) {
                throw new Error(`Invalid currency: ${currency}`);
            }
            
            const hourlyFromDate = new Date();
            hourlyFromDate.setHours(hourlyFromDate.getHours() - 1);

            const queryString = `
                query HistoricalBitcoinPrices {
                    currentPrice: bitcoinCurrentPrice(currency:${currency}, priceType:buy),
                    historicalBitcoinPrices(
                        currency: ${currency}
                        fromDate: "${fromDate.toISOString()}"
                        toDate: "${toDate}"
                        granularity: ${granularity}
                        maxDataPoints: ${maxDataPoints}
                    ) {
                        prices {
                            datetime
                            bitcoinPrice
                        }
                    }
                    latestHourly: historicalBitcoinPrices(
                        currency: ${currency}
                        fromDate: "${hourlyFromDate.toISOString()}"
                        toDate: "${toDate}"
                        granularity: HOURLY
                        maxDataPoints: 1
                    ) {
                        prices {
                            datetime
                            bitcoinPrice
                        }
                    }
                }
            `;
            
            const requestBody = {
                query: queryString
            };
            
            const res = await fetch('https://api-v3.relai.ch/graphql', {
                method: 'POST',
                headers: {
                    'app-version': '4.0.0',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody),
                signal: this.settings.abortController.signal
            });

            clearTimeout(timeoutId);
            
            const result = await res.json()
            console.log(result);
            
            if (!res.ok) {
                console.error('HTTP error:', res.status, result);
                throw new Error(`HTTP ${res.status}`);
            }
            
            if (result.errors) {
                throw new Error(`GraphQL error: ${result.errors[0]?.message || 'Unknown error'}`);
            }
            
            if (!result.data?.historicalBitcoinPrices) {
                throw new Error('Invalid API response');
            }
            
            const prices = result.data.historicalBitcoinPrices?.prices;
            const currentPrice = result.data.currentPrice;
            const latestHourlyPrices = result.data.latestHourly?.prices;
            
            if (!prices) {
                return { prices: [], currentPrice: currentPrice || null };
            }
            
            const pricesArray = Array.isArray(prices) ? prices : Object.values(prices);
            
            if (pricesArray.length === 0) {
                return { prices: [], currentPrice: currentPrice || null };
            }

            const history = pricesArray.map(price => ({
                datetime: price.datetime,
                price: price.bitcoinPrice
            }));

            history.sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
            
            if (latestHourlyPrices && latestHourlyPrices.length > 0) {
                const hourlyPrice = latestHourlyPrices[0];
                history.push({
                    datetime: hourlyPrice.datetime,
                    price: hourlyPrice.bitcoinPrice
                });
            }

            const processedHistory = forOrangeLine 
                ? history 
                : this.aggregatePriceData(history, startYear);

            this.settings.dataCache[cacheKey] = {
                data: { prices: processedHistory, currentPrice: currentPrice || null },
                timestamp: Date.now()
            };
            
            this.manageCacheSize();
            return { prices: processedHistory, currentPrice: currentPrice || null };

        } catch (error) {
            clearTimeout(timeoutId);
            
            if (error.name === 'AbortError') {
                return { prices: [], currentPrice: null };
            }
            
            if (this.settings.dataCache[cacheKey]) {
                console.warn('Using cached data due to error');
                return this.settings.dataCache[cacheKey].data;
            }
            
            console.error('Error fetching Bitcoin historical prices:', error);
            return { prices: [], currentPrice: null };
        }
    },

    getFormValues() {
        let currency = this.settings.defaultCurrency;
        
        const currencyInput = document.querySelector('#bitcoin-currency') || this.settings.selectCurrency;
        if (currencyInput) {
            currency = currencyInput.value || currency;
            if (!currency && currencyInput._choicesjs) {
                const choiceValue = currencyInput._choicesjs.getValue(true);
                currency = choiceValue || currency;
            }
        }
        
        if (!currency || !['USD', 'EUR', 'CHF'].includes(currency)) {
            currency = this.settings.defaultCurrency;
        }
        
        return {
            investedAmount: parseFloat(this.settings.inputAmount?.value) || this.settings.defaultAmount,
            interval: this.settings.selectInterval?.value || this.settings.defaultInterval,
            currency: currency
        };
    },

    calculateInvestments(startYear, formValues, historicalPrices) {
        if (!historicalPrices || historicalPrices.length === 0) {
            return { currentValueData: [], investedAmountData: [], labels: [], bitcoinPriceData: [] };
        }

        const maxDataPoints = 300;
        const validPrices = historicalPrices.filter(p => p?.price > 0);
        if (validPrices.length === 0) {
            return { currentValueData: [], investedAmountData: [], labels: [], bitcoinPriceData: [] };
        }
        const limitedPrices = validPrices.length > maxDataPoints
            ? validPrices.filter((_, i) => i % Math.ceil(validPrices.length / maxDataPoints) === 0)
            : validPrices;

        const currentYear = new Date().getFullYear();
        const isCurrentYear = startYear === currentYear;
        const interval = formValues.interval;
        const investmentAmount = formValues.investedAmount;

        const currentValueData = [];
        const investedAmountData = [];
        const bitcoinPriceData = [];
        const labels = [];

        const startDate = new Date(limitedPrices[0].datetime);

        for (let i = 0; i < limitedPrices.length; i++) {
            const currentDate = new Date(limitedPrices[i].datetime);
            const price = limitedPrices[i].price;

            bitcoinPriceData.push(price);

            if (isCurrentYear) {
                labels.push(currentDate.toLocaleDateString('en-US', { month: 'short' }));
            } else {
                labels.push(currentDate.getFullYear().toString());
            }

            const totalInvestmentsMade = this.getInvestmentCountByDate(startDate, currentDate, interval);
            const totalInvested = totalInvestmentsMade * investmentAmount;

            let totalBitcoin = 0;
            for (let j = 0; j <= i; j++) {
                const dateJ = new Date(limitedPrices[j].datetime);
                const datePrev = j > 0 ? new Date(limitedPrices[j - 1].datetime) : startDate;
                const countAtJ = this.getInvestmentCountByDate(startDate, dateJ, interval);
                const countAtPrev = j === 0 ? 0 : this.getInvestmentCountByDate(startDate, datePrev, interval);
                const investmentsInThisPeriod = Math.max(0, countAtJ - countAtPrev);

                totalBitcoin += (investmentsInThisPeriod * investmentAmount) / limitedPrices[j].price;
            }
            
            const currentValue = totalBitcoin * price;
            
            currentValueData.push(Math.round(currentValue));
            investedAmountData.push(Math.round(totalInvested));
        }

        return { currentValueData, investedAmountData, labels, bitcoinPriceData };
    },

    async getInvestmentData(startDateOrYear) {
        const formValues = this.getFormValues();
        
        const currency = formValues.currency;
        
        const startYear = new Date(startDateOrYear).getFullYear();

        const bitcoinData = await this.getBitcoinHistoricalPrices(currency, startDateOrYear, true);
        const rawBitcoinPrices = bitcoinData.prices || [];
        const currentPrice = bitcoinData.currentPrice;
        
        if (!rawBitcoinPrices || rawBitcoinPrices.length === 0) {
            return {
                labels: [],
                datasets: [
                    {
                        label: this.settings.labelInvested,
                        data: [],
                        borderColor: 'transparent',
                        backgroundColor: this.createChartGradient(),
                        fill: true,
                        yAxisID: 'yInvested',
                        order: 2,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                    },
                    {
                        label: this.settings.labelPortfolio,
                        data: [],
                        borderColor: this.CONSTANTS.ORANGE_COLOR,
                        borderWidth: 3,
                        backgroundColor: 'transparent',
                        pointBackgroundColor: this.CONSTANTS.ACCENT_COLOR,
                        pointBorderWidth: 2,
                        pointBorderColor: '#fff',
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        fill: false,
                        tension: 0,
                        yAxisID: 'yBitcoin',
                        order: 1,
                    },
                    {
                        label: 'Current transparent',
                        data: [],
                        borderColor: 'transparent',
                        backgroundColor: 'transparent',
                        hidden: true,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        yAxisID: 'yBitcoin',
                        order: 3,
                    },
                ],
            };
        }
        
        const fromDate = new Date(startDateOrYear);
        const monthsDiff = (new Date().getFullYear() - fromDate.getFullYear()) * 12 + (new Date().getMonth() - fromDate.getMonth());
        const useMonthLabels = monthsDiff <= 12;

        const bitcoinPriceData = [];
        const labels = [];
        const datetimes = [];
        
        let lastLabel = null;
        
        rawBitcoinPrices.forEach((price, index) => {
            const date = new Date(price.datetime);
            bitcoinPriceData.push(price.price);
            datetimes.push(price.datetime);
            
            let currentLabel = '';
            
            if (useMonthLabels) {
                currentLabel = date.toLocaleDateString('en-US', { month: 'short' });
            } else {
                currentLabel = date.getFullYear().toString();
            }
            
            // edge case, Hide first label if index 0 has different year than index 1
            if (index === 0 && rawBitcoinPrices.length > 1) {
                const nextDate = new Date(rawBitcoinPrices[1].datetime);
                if (date.getFullYear() !== nextDate.getFullYear()) {
                    labels.push('');
                    lastLabel = currentLabel;
                    return;
                }
            }
            
            if (currentLabel !== lastLabel) {
                labels.push(currentLabel);
                lastLabel = currentLabel;
            } else {
                labels.push('');
            }
        });
        
        
        const { currentValueData, investedAmountData, totalBitcoinData } = this.calculateInvestmentsFromRawPrices(
            startYear,
            formValues,
            rawBitcoinPrices,
            startDateOrYear
        );

        if (currentValueData && currentValueData.length > 0 && investedAmountData && investedAmountData.length > 0) {
            const finalInvestedAmount = investedAmountData[investedAmountData.length - 1];
            const totalBitcoin = totalBitcoinData && totalBitcoinData.length > 0 ? totalBitcoinData[totalBitcoinData.length - 1] : 0;

            const actualCurrentPrice = currentPrice !== null && currentPrice !== undefined ? currentPrice : rawBitcoinPrices[rawBitcoinPrices.length - 1].price;
            const finalCurrentValue = Math.round(totalBitcoin * actualCurrentPrice);
            currentValueData[currentValueData.length - 1] = finalCurrentValue;

            if (finalCurrentValue !== undefined && finalInvestedAmount !== undefined) {
                const profitLoss = finalCurrentValue - finalInvestedAmount;
                const profitLossPercentage = finalInvestedAmount > 0
                    ? ((finalCurrentValue - finalInvestedAmount) / finalInvestedAmount) * 100
                    : 0;

                this.updateProfitLossDisplay(profitLoss, profitLossPercentage, formValues.currency);
                this.updateBitcoinOwnDisplay(totalBitcoin, profitLossPercentage, formValues.currency);
            }
        }

        return {
            labels: labels,
            datasets: [
                {
                    label: this.settings.labelInvested,
                    data: investedAmountData,
                    borderColor: 'transparent',
                    backgroundColor: this.createChartGradient(),
                    legendBackground: `linear-gradient(to bottom, ${this.CONSTANTS.GRADIENT_START}, ${this.CONSTANTS.GRADIENT_END})`,
                    fill: true,
                    yAxisID: 'yInvested',
                    order: 2,
                    datetimes: datetimes,
                    pointRadius: 0,
                    pointHoverRadius: 0,
                },
                {
                    label: this.settings.labelPortfolio,
                    data: currentValueData,
                    borderColor: this.CONSTANTS.ORANGE_COLOR,
                    borderWidth: 3,
                    backgroundColor: 'transparent',
                    pointBackgroundColor: this.CONSTANTS.ACCENT_COLOR,
                    pointBorderWidth: 2,
                    pointBorderColor: '#fff',
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    fill: false,
                    tension: 0,
                    yAxisID: 'yBitcoin',
                    order: 1,
                    datetimes: datetimes,
                },
                {
                    label: 'Current transparent',
                    data: bitcoinPriceData,
                    borderColor: 'transparent',
                    backgroundColor: 'transparent',
                    hidden: true,
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    yAxisID: 'yBitcoin',
                    order: 3,
                },
            ],
        };
    },
    
    calculateInvestmentsFromRawPrices(startYear, formValues, rawPrices, requestedStartDate) {
        const validPrices = rawPrices?.filter(p => p?.price > 0);
        if (!validPrices || validPrices.length === 0) {
            return { currentValueData: [], investedAmountData: [], totalBitcoinData: [] };
        }

        const investmentAmount = formValues.investedAmount;
        const interval = formValues.interval;

        const currentValueData = [];
        const investedAmountData = [];
        const totalBitcoinData = [];

        // Use the user-requested start date as the DCA anchor so that API data points
        // landing slightly before the requested date don't add phantom investment periods.
        const startDate = requestedStartDate ? new Date(requestedStartDate) : new Date(validPrices[0].datetime);

        for (let i = 0; i < validPrices.length; i++) {
            const currentDate = new Date(validPrices[i].datetime);
            const price = validPrices[i].price;

            const totalInvestmentsMade = this.getInvestmentCountByDate(startDate, currentDate, interval);
            const totalInvested = totalInvestmentsMade * investmentAmount;

            let totalBitcoin = 0;
            for (let j = 0; j <= i; j++) {
                const dateJ = new Date(validPrices[j].datetime);
                const datePrev = j > 0 ? new Date(validPrices[j - 1].datetime) : startDate;

                const countAtJ = this.getInvestmentCountByDate(startDate, dateJ, interval);
                const countAtPrev = j === 0 ? 0 : this.getInvestmentCountByDate(startDate, datePrev, interval);
                const investmentsInThisPeriod = Math.max(0, countAtJ - countAtPrev);

                totalBitcoin += (investmentsInThisPeriod * investmentAmount) / validPrices[j].price;
            }
            
            const currentValue = totalBitcoin * price;
            
            currentValueData.push(Math.round(currentValue));
            investedAmountData.push(Math.round(totalInvested));
            totalBitcoinData.push(totalBitcoin);
        }

        return { currentValueData, investedAmountData, totalBitcoinData };
    },

    formatNumberDisplay(value) {
        const num = typeof value === 'number' ? value : parseFloat(value);
        if (!isFinite(num)) return '';
        return num
            .toFixed(2)
            .replace('.', ',')
            .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    },

    formatCurrency(value, currency = 'EUR', symbol = true) {
        const num =
            typeof value === 'number'
                ? value
                : typeof value === 'string'
                ? parseFloat(value.replace(/,/g, ''))
                : NaN;
    
        if (!isFinite(num)) return '';
    
        const formattedNumber = this.formatNumberDisplay(num);
    
        if (symbol) {
            const symbols = {
                USD: '$',
                EUR: '€',
                CHF: 'CHF ',
            };
    
            return `${symbols[currency] || ''}${formattedNumber}`;
        }
    
        return `${formattedNumber} ${currency}`;
    },

    setBitcoinValueDisplay(value) {
        if (this.settings.bitcoinValueElement && value != null) {
            this.settings.bitcoinValueElement.textContent = this.formatNumberDisplay(value);
        }
    },

    formatYAxisTick(value, index, values) {
        const num = typeof value === 'number' ? value : parseFloat(value);
        if (!isFinite(num)) return '';
        const showCurrency = values && (index === 0 || index === values.length - 1);
        const prefix = showCurrency ? (this.formatCurrency(0, this.getFormValues().currency).replace(/\d.*$/, '') || '$') : '';
        if (num === 0) return `${prefix}0`;
        if (Math.abs(num) >= 1000) return `${prefix}${Math.round(num / 1000)}k`;
        return `${prefix}${Math.round(num)}`;
    },

    getYAxisTickCallback() {
        return (value, index, values) => this.formatYAxisTick(value, index, values);
    },

    tooltipShadow() {
        return {
            id: 'tooltipShadow',
            beforeTooltipDraw(chart) {
                const { ctx, tooltip } = chart;
                
                if (tooltip && tooltip.opacity > 0) {
                    ctx.save();
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 16;
                    ctx.shadowBlur = 16;
                    ctx.shadowColor = 'rgba(12, 12, 13, 0.1)';
                }
            },
            afterTooltipDraw(chart) {
                const { ctx, tooltip } = chart;
                
                if (tooltip && tooltip.opacity > 0) {
                    ctx.restore();
                }
            }
        };
    },

    drawCrosshair() {
        return {
            id: 'crosshair',
            beforeTooltipDraw(chart) {
                const { ctx, tooltip, chartArea: { top, bottom, left, right } } = chart;
                
                if (tooltip && tooltip.opacity > 0 && tooltip.dataPoints && tooltip.dataPoints.length > 0) {
                    const activePoint = tooltip.dataPoints[0];
                    const x = activePoint.element.x;
                    const y = activePoint.element.y;

                    ctx.save();
                    
                    ctx.strokeStyle = '#B4B7BD';
                    ctx.lineWidth = 1;
                    ctx.setLineDash([5, 5]);

                    ctx.beginPath();
                    ctx.moveTo(x, top);
                    ctx.lineTo(x, bottom);
                    ctx.stroke();

                    ctx.beginPath();
                    ctx.moveTo(left, y);
                    ctx.lineTo(right, y);
                    ctx.stroke();

                    ctx.setLineDash([]);
                    ctx.beginPath();
                    ctx.arc(x, y, 8.5, 0, 2 * Math.PI);
                    ctx.fillStyle = '#14217E';
                    ctx.fill();
                    ctx.strokeStyle = '#fff';
                    ctx.lineWidth = 2;
                    ctx.stroke();

                    ctx.restore();
                }
            }
        };
    },

    getTooltipPosition(chart, tooltip, tooltipEl) {
        const position = chart.canvas.getBoundingClientRect();
        const offsetX = 20;
        const offsetY = 40;
        const chartWidth = chart.width;
        const isRightSide = tooltip.caretX > chartWidth * 0.6;
        const tooltipWidth = tooltipEl.offsetWidth || 200;
        
        const left = isRightSide 
            ? position.left + window.pageXOffset + tooltip.caretX - tooltipWidth - offsetX
            : position.left + window.pageXOffset + tooltip.caretX + offsetX;
        
        return {
            left: left + 'px',
            top: position.top + window.pageYOffset + tooltip.caretY + offsetY + 'px'
        };
    },

    externalTooltipHandler(context, formValues) {
        const { chart, tooltip } = context;
        let tooltipEl = document.getElementById('chartjs-tooltip');

        if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip';
            tooltipEl.classList.add('bitcoin-calculator-tooltip');
            document.body.appendChild(tooltipEl);
        }

        if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
        }

        if (tooltip.body) {
            const dataIndex = tooltip.dataPoints[0].dataIndex;
            const bitcoinDataset = chart.data.datasets.find(d => d.label === this.settings.labelPortfolio);
            const investedDataset = chart.data.datasets.find(d => d.label === this.settings.labelInvested);
            const datetimes = tooltip.dataPoints[0].dataset.datetimes;

            const date = new Date(datetimes[dataIndex]);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            let title = `${day}.${month}.${year}`;

            // Show hours if time is not midnight (00:00)
            const hours = date.getHours();
            const minutes = date.getMinutes();
            if (hours !== 0 || minutes !== 0) {
                const hoursStr = String(hours).padStart(2, '0');
                const minutesStr = String(minutes).padStart(2, '0');
                title = `${day}.${month}.${year} ${hoursStr}:${minutesStr}`;
            }

            const portfolioValue = this.formatCurrency(bitcoinDataset.data[dataIndex], formValues.currency);
            const investedAmount = this.formatCurrency(investedDataset.data[dataIndex], formValues.currency);

            const currentValue = bitcoinDataset.data[dataIndex];
            const invested = investedDataset.data[dataIndex];
            const profitLoss = currentValue - invested;
            const percentage = invested > 0 ? ((currentValue - invested) / invested) * 100 : 0;
            
            const isPositive = profitLoss > 0;
            const isZero = Math.abs(percentage) < 0.01;
            const sign = isZero ? '' : (isPositive ? '+' : '-');
            
            const profitLossClass = isZero ? 'text-gray' : (isPositive ? 'text-green' : 'text-red');
            const formattedPercentage = Math.abs(percentage).toFixed(2);
            
            const profitIcon = `<svg width="14" height="14" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" rx="16" fill="#E5F5EA"/>
                <path d="M21.25 11.5C21.25 11.0858 20.9142 10.75 20.5 10.75L13.75 10.75C13.3358 10.75 13 11.0858 13 11.5C13 11.9142 13.3358 12.25 13.75 12.25H19.75V18.25C19.75 18.6642 20.0858 19 20.5 19C20.9142 19 21.25 18.6642 21.25 18.25L21.25 11.5ZM11.5 20.5L12.0303 21.0303L21.0303 12.0303L20.5 11.5L19.9697 10.9697L10.9697 19.9697L11.5 20.5Z" fill="#22C55E"/>
            </svg>`;
            
            const orangeDot = `<svg width="14" height="14" viewBox="0 0 14 14"><circle cx="7" cy="7" r="7" fill="#F98800"/></svg>`;
            const grayDot = `<svg width="14" height="14" viewBox="0 0 14 14"><circle cx="7" cy="7" r="7" fill="#B4B7BD"/></svg>`;
            
            tooltipEl.innerHTML = `
                <div class="bitcoin-calculator-tooltip-title">${title}</div>
                <div class="bitcoin-calculator-tooltip-line">${orangeDot}${portfolioValue}</div>
                <div class="bitcoin-calculator-tooltip-line">${grayDot}${investedAmount}</div>
                <div class="bitcoin-calculator-tooltip-profit ${profitLossClass}">
                    ${profitIcon}
                    <span>${sign}${formattedPercentage}%</span>
                </div>
            `;
        }

        const pos = this.getTooltipPosition(chart, tooltip, tooltipEl);
        tooltipEl.style.opacity = 1;
        tooltipEl.style.left = pos.left;
        tooltipEl.style.top = pos.top;
    },

    async drawChart() {
        if (!this.settings.canvas) return;
        
        if (this.settings.isUpdating) return;

        this.settings.isUpdating = true;

        try {
            const formValues = this.getFormValues();
            const ctx = this.settings.canvas.getContext('2d');
            const currentYear = new Date().getFullYear();
            let startDateOrYear = currentYear; 
            
            if (this.settings.selectStartYear && this.settings.selectStartYear.value) {
                startDateOrYear = this.settings.selectStartYear.value;
            }

            const investmentData = await this.getInvestmentData(startDateOrYear);

            const portfolioValueData = investmentData.datasets[1].data;
            const investedAmountData = investmentData.datasets[0].data;
            const allValues = [...portfolioValueData, ...investedAmountData];
            const maxValue = allValues.length ? Math.max(...allValues) : 0;
            const yAxisMax = Math.ceil(maxValue * 1.1 / 10000) * 10000 || 10000;

            const config = {
                type: 'line',
                data: investmentData,
                options: {
                    layout: {
                        padding: 10
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: {
                        duration: 0
                    },
                    interaction: {
                        mode: 'index',
                        intersect: false,
                    },
                    plugins: {

                        htmlLegend: {
                            containerID: 'bitcoin-calculator__legend',
                        },
                        legend: {
                            display: false,
                        },
                        tooltip: {
                            enabled: false,
                            external: (context) => this.externalTooltipHandler(context, formValues)
                        },
                    },
                    scales: {
                        x: {
                            display: true,
                            grid: {
                                display: false, 
                                drawBorder: false,
                            },
                            ticks: {
                                font: {
                                    size: 12,
                                    family: this.CONSTANTS.FONT_FAMILY,
                                    weight: 'bolder',
                                },
                                color: this.CONSTANTS.TEXT_COLOR,
                                autoSkip: false,
                                maxRotation: 0,
                                minRotation: 0,
                            }
                        },
                        yBitcoin: {
                            type: 'linear',
                            position: 'left',
                            min: 0,
                            max: yAxisMax,
                            border: { display: false },
                            ticks: {
                                stepSize: 10000,
                                maxTicksLimit: 8,
                                font: {
                                    size: 12,
                                    family: this.CONSTANTS.FONT_FAMILY,
                                    weight: 'bolder'
                                },
                                color: this.CONSTANTS.TEXT_COLOR,
                                callback: this.getYAxisTickCallback(),
                            },
                            grid: {
                                color: this.CONSTANTS.GRID_COLOR,
                                drawBorder: false,
                            }
                        },
                        yInvested: {
                            type: 'linear',
                            position: 'right',
                            display: false,
                            min: 0,
                            max: yAxisMax,
                            grid: {
                                display: false,
                            }
                        },
                    },
                },
            };

            if (this.settings.chartInstance) {
                this.settings.chartInstance.destroy();
                this.settings.chartInstance = null;
            }

            config.plugins = [
                this.tooltipShadow(),
                this.drawCrosshair(),
                htmlLegendPlugin
            ];

            const chartInstance = new Chart(ctx, config);
            this.settings.chartInstance = chartInstance;

            const lastPortfolioValue = portfolioValueData.length > 0 ? portfolioValueData[portfolioValueData.length - 1] : null;
            this.setBitcoinValueDisplay(lastPortfolioValue);

            this.updateTableValues(investmentData);
            
        } catch (error) {
            console.error('Error drawing calculator chart:', error);
        } finally {
            this.settings.isUpdating = false;
        }
    },

    async updateChart() {
        if (this.settings.isUpdating) return;
        if (!this.settings.chartInstance) return;

        this.settings.isUpdating = true;

        try {
            const startDateOrYear = this.settings.selectStartYear?.value || new Date().getFullYear();
            const formValues = this.getFormValues();
            
            const newData = await this.getInvestmentData(startDateOrYear);
            
            if (!newData || !newData.datasets || newData.datasets.length === 0) {
                console.error('Invalid data received');
                return;
            }
            
            this.settings.chartInstance.data.labels = newData.labels;
            this.settings.chartInstance.data.datasets = newData.datasets;
            
            const portfolioValueData = newData.datasets[1].data;
            const investedAmountData = newData.datasets[0].data;
            const allValues = [...portfolioValueData, ...investedAmountData];
            const maxValue = allValues.length ? Math.max(...allValues) : 0;
            const yAxisMax = Math.ceil(maxValue * 1.1 / 10000) * 10000 || 10000;

            this.settings.chartInstance.options.scales.yBitcoin.max = yAxisMax;
            this.settings.chartInstance.options.scales.yBitcoin.ticks.callback = this.getYAxisTickCallback();
            this.settings.chartInstance.options.scales.yInvested.max = yAxisMax;
            
            this.settings.chartInstance.options.plugins.tooltip.external = (context) => this.externalTooltipHandler(context, formValues);
            
            this.settings.chartInstance.options.animation = { duration: 0 };
            this.settings.chartInstance.update('none');

            const lastPortfolioValue = portfolioValueData.length > 0 ? portfolioValueData[portfolioValueData.length - 1] : null;
            this.setBitcoinValueDisplay(lastPortfolioValue);

            this.updateTableValues(newData);
        } catch (error) {
            console.error('Error updating chart:', error);
        } finally {
            this.settings.isUpdating = false;
        }
    },

    updateBitcoinOwnDisplay(totalBitcoin, percentage, currency) {
        if (!this.settings.bitcoinOwnValue) return;
        const formattedBtc = typeof totalBitcoin === 'number' && totalBitcoin >= 0
            ? totalBitcoin.toFixed(8).replace('.', ',')
            : '0,00000000';
        this.settings.bitcoinOwnValue.textContent = `${formattedBtc} ${this.settings.labelBtc}`;
    },

    updateProfitLossDisplay(profitLoss, percentage, currency) {
        const profitLossContainer = this.settings.profitLossContainer;
        const profitLossAmount = this.settings.profitLossAmount;
        const profitLossPercentage = this.settings.profitLossPercentage;

        if (!profitLossContainer || !profitLossAmount || !profitLossPercentage) {
            return;
        }

        const formattedAmount = this.formatNumberDisplay(Math.abs(profitLoss));
        const formattedPercentage = Math.abs(percentage).toFixed(2);
        const isPositive = profitLoss > 0;
        const isZero = Number(formattedPercentage) === 0;

        const sign = isZero ? '' : (isPositive ? '+' : '-');
        
        profitLossAmount.textContent = `${sign}${formattedAmount}`;
        profitLossPercentage.textContent = `(${sign}${formattedPercentage}%)`;

        profitLossContainer.classList.remove('is-hidden');
        profitLossContainer.classList.toggle('text-gray', isZero);
        profitLossContainer.classList.toggle('text-green', !isZero && isPositive);
        profitLossContainer.classList.toggle('text-red', !isZero && !isPositive);
    },

    updateTableValues(investmentData) {
        try {
            const formValues = this.getFormValues();
            const finalInvestedAmount = investmentData.datasets[0].data[investmentData.datasets[0].data.length - 1];

            if (this.settings.currencyTable) {
                this.settings.currencyTable.forEach(currencyItem => {
                    currencyItem.textContent = formValues.currency;
                });
            }

            if (this.settings.amountTable) {
                this.settings.amountTable.textContent = `${this.formatNumberDisplay(finalInvestedAmount)} ${formValues.currency}`;
            }
        } catch (error) {
            console.error('Error updating table values:', error);
        }
    },

    listenForPriceUpdates() {
        const priceUpdateHandler = (event) => {
            this.handlePriceUpdate(event.detail);
        };
        
        window.addEventListener('bitcoinPriceUpdate', priceUpdateHandler);
        this.settings.eventListeners.push({
            element: window,
            event: 'bitcoinPriceUpdate',
            handler: priceUpdateHandler
        });
    },

    async handlePriceUpdate(updateData) {
        if (!this.settings.chartInstance || !updateData?.prices || this.settings.isUpdating) return;

        // Store globally for reuse when changing start year
        window.lastBitcoinPriceUpdate = updateData;

        const formValues = this.getFormValues();
        const currency = formValues.currency;
        const priceData = updateData.prices[currency];

        if (!priceData) return;

        const startDateOrYear = this.settings.selectStartYear?.value || new Date().getFullYear();
        const cacheKey = `historical-${currency}-${startDateOrYear}-raw`;
        const cachedData = this.settings.dataCache[cacheKey];

        if (!cachedData?.data?.prices) return;

        // Check if the new price is the same as the last price
        const lastPrice = cachedData.data.prices.length > 0
            ? cachedData.data.prices[cachedData.data.prices.length - 1].price
            : null;

        if (lastPrice !== null && priceData.value === lastPrice) {
            return; // Don't append if value is the same
        }

        this.settings.isUpdating = true;

        try {
            // Replace previous live entry or add new one to prevent cache bloat
            const newPriceEntry = {
                datetime: priceData.datetime,
                price: priceData.value,
                _isLive: true
            };

            const lastCacheEntry = cachedData.data.prices[cachedData.data.prices.length - 1];
            const isReplacingLive = lastCacheEntry?._isLive === true;

            if (isReplacingLive) {
                cachedData.data.prices[cachedData.data.prices.length - 1] = newPriceEntry;
            } else {
                cachedData.data.prices.push(newPriceEntry);
            }

            // Recalculate investment data with new price
            const rawPrices = cachedData.data.prices;
            const startYear = new Date(startDateOrYear).getFullYear();
            const fromDate = new Date(startDateOrYear);
            const monthsDiff = (new Date().getFullYear() - fromDate.getFullYear()) * 12 + (new Date().getMonth() - fromDate.getMonth());

            const { currentValueData, investedAmountData, totalBitcoinData } = this.calculateInvestmentsFromRawPrices(
                startYear,
                formValues,
                rawPrices,
                startDateOrYear
            );

            // Update chart datasets
            const investedDataset = this.settings.chartInstance.data.datasets.find(d => d.label === this.settings.labelInvested);
            const bitcoinDataset = this.settings.chartInstance.data.datasets.find(d => d.label === this.settings.labelPortfolio);
            const currentValueDataset = this.settings.chartInstance.data.datasets.find(d => d.label === 'Current transparent');
            const labels = this.settings.chartInstance.data.labels;

            if (investedDataset && bitcoinDataset && currentValueDataset) {
                const lastInvested = investedAmountData[investedAmountData.length - 1];
                const lastCurrentValue = currentValueData[currentValueData.length - 1];

                if (isReplacingLive) {
                    // Replace last chart point (previous live update)
                    const lastIdx = investedDataset.data.length - 1;
                    investedDataset.data[lastIdx] = lastInvested;
                    bitcoinDataset.data[lastIdx] = lastCurrentValue;
                    currentValueDataset.data[lastIdx] = priceData.value;
                    if (investedDataset.datetimes) investedDataset.datetimes[lastIdx] = priceData.datetime;
                    if (bitcoinDataset.datetimes) bitcoinDataset.datetimes[lastIdx] = priceData.datetime;
                    if (currentValueDataset.datetimes) currentValueDataset.datetimes[lastIdx] = priceData.datetime;
                } else {
                    // Append new data point
                    const date = new Date(priceData.datetime);
                    const label = monthsDiff <= 12
                        ? date.toLocaleDateString('en-US', { month: 'short' })
                        : date.getFullYear().toString();
                    const lastNonEmptyLabel = [...labels].reverse().find(l => l !== '') || '';
                    labels.push(lastNonEmptyLabel === label ? '' : label);

                    investedDataset.data.push(lastInvested);
                    bitcoinDataset.data.push(lastCurrentValue);
                    currentValueDataset.data.push(priceData.value);
                    if (investedDataset.datetimes) investedDataset.datetimes.push(priceData.datetime);
                    if (bitcoinDataset.datetimes) bitcoinDataset.datetimes.push(priceData.datetime);
                    if (currentValueDataset.datetimes) currentValueDataset.datetimes.push(priceData.datetime);
                }
            }

            // Recalculate y-axis max from both datasets
            const portfolioValueData = this.settings.chartInstance.data.datasets[1].data;
            const investedAmountDataAll = this.settings.chartInstance.data.datasets[0].data;
            const allValues = [...portfolioValueData, ...investedAmountDataAll];
            const maxValue = allValues.length ? Math.max(...allValues) : 0;
            const yAxisMax = Math.ceil(maxValue * 1.1 / 10000) * 10000 || 10000;

            this.settings.chartInstance.options.scales.yBitcoin.max = yAxisMax;
            this.settings.chartInstance.options.scales.yInvested.max = yAxisMax;

            // Update profit/loss display
            const finalInvestedAmount = investedAmountData[investedAmountData.length - 1];
            const totalBitcoin = totalBitcoinData[totalBitcoinData.length - 1];
            const finalCurrentValue = Math.round(totalBitcoin * priceData.value);
            this.setBitcoinValueDisplay(finalCurrentValue);

            if (finalCurrentValue !== undefined && finalInvestedAmount !== undefined) {
                const profitLoss = finalCurrentValue - finalInvestedAmount;
                const profitLossPercentage = finalInvestedAmount > 0
                    ? ((finalCurrentValue - finalInvestedAmount) / finalInvestedAmount) * 100
                    : 0;

                this.updateProfitLossDisplay(profitLoss, profitLossPercentage, currency);
                this.updateBitcoinOwnDisplay(totalBitcoin, profitLossPercentage, currency);
            }

            // Keep table values in sync with profit/loss display
            this.updateTableValues(this.settings.chartInstance.data);

            // Update chart without animation
            this.settings.chartInstance.options.animation = { duration: 0 };
            this.settings.chartInstance.update('none');
        } finally {
            this.settings.isUpdating = false;
        }
    }
};

window.addEventListener('DOMContentLoaded', () => calculator.init());
window.addEventListener('beforeunload', () => calculator.destroy());